import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Select, Button, Upload, Alert } from "antd";
import { saveAs } from "file-saver";
import { IconUpload } from "assets";
import { branchApi } from "services/branch";
import { masterDataApi } from "services/masterData";
import "./ModalRegisterBranch.scss";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const XLSX_EXT = ".xlsx";

const ModalRegisterBranch = ({ visible, onClose, onSuccess }) => {
  const [offices, setOffices] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (!visible) return;
    setErrors([]);
    setErrorMessage(null);
    setFile(null);
    setSelectedOffice(null);
    masterDataApi
      .getOperationPageOffices()
      .then((res) => {
        const opts = (res || []).map((o) => ({
          value: o.value ?? o.office_cd,
          label: o.label ?? o.office_nm,
        }));
        setOffices(opts);
      })
      .catch(() => setErrorMessage("営業所一覧の取得に失敗しました"));
  }, [visible]);

  const handleFileSelect = (selectedFile) => {
    setErrors([]);
    setErrorMessage(null);

    if (!selectedFile.name.toLowerCase().endsWith(XLSX_EXT)) {
      setErrorMessage("Excelファイル(.xlsx)を選択してください");
      return false;
    }
    if (selectedFile.size > MAX_FILE_SIZE) {
      setErrorMessage("ファイルサイズは5MB以下にしてください");
      return false;
    }
    setFile(selectedFile);
    return false;
  };

  const handleSubmit = async () => {
    if (!selectedOffice) {
      setErrorMessage("営業所を選択してください");
      return;
    }
    if (!file) return;
    setSubmitting(true);
    setErrors([]);
    setErrorMessage(null);
    try {
      const result = await branchApi.upload(selectedOffice, file);
      if (result.geocode_failed > 0) {
        Modal.warning({
          title: `${result.geocoded}件登録、${result.geocode_failed}件は座標取得失敗`,
          width: 600,
          content: (
            <div>
              <p>以下の住所は地図上に表示されません。住所を修正して再アップロードしてください。</p>
              <ul style={{ maxHeight: 300, overflowY: "auto" }}>
                {(result.geocode_failures || []).map((f) => (
                  <li key={f.row}>
                    行 {f.row}: {f.branch_nm} — {f.address}
                  </li>
                ))}
              </ul>
            </div>
          ),
        });
      } else {
        Modal.success({ content: `${result.total}件登録しました` });
      }
      onSuccess?.(selectedOffice);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.status === "validation_failed") {
        setErrors(detail.errors || []);
      } else {
        setErrorMessage("アップロードに失敗しました");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async () => {
    if (!selectedOffice) {
      setErrorMessage("営業所を選択してください");
      return;
    }

    setDownloading(true);
    setErrorMessage(null);
    try {
      const res = await branchApi.download(selectedOffice);
      saveAs(res.data, `branches_${selectedOffice}.xlsx`);
    } catch {
      setErrorMessage("ダウンロードに失敗しました");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      wrapClassName="modal-register-branch-wrap"
      title={
        <div className="modal-register-branch-title">
          <span>拠点登録</span>
          <Button
            size="small"
            onClick={handleDownload}
            loading={downloading}
            disabled={!selectedOffice}
          >
            ダウンロード
          </Button>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={480}
      footer={null}
    >
      <div className="modal-register-branch">
        <div className="form-row">
          <label htmlFor="office-select" className="required-label">営業所</label>
          <Select
            id="office-select"
            placeholder="営業所を選択"
            options={offices}
            value={selectedOffice}
            onChange={setSelectedOffice}
            style={{ width: "100%" }}
          />
        </div>

        <div className="form-row upload-row">
          <label>添付資料</label>
          <Upload.Dragger
            beforeUpload={handleFileSelect}
            showUploadList={false}
            accept=".xlsx"
            multiple={false}
            className="branch-upload-dragger"
          >
            <p className="ant-upload-drag-icon">
              <IconUpload />
            </p>
            <p className="upload-text">
              {file ? (
                file.name
              ) : (
                <>
                  ここにドラッグ＆ドロップ
                  <br />
                  または<span>ファイルを選択</span>
                </>
              )}
            </p>
            <div className="upload-hint">※ Excelのみアップロード可能</div>
          </Upload.Dragger>
        </div>

        {submitting && (
          <Alert
            type="info"
            message="処理中... ファイルをアップロード・処理しています"
            showIcon
          />
        )}

        {errorMessage && (
          <Alert
            type="error"
            message={errorMessage}
            showIcon
            closable
            onClose={() => setErrorMessage(null)}
          />
        )}

        {errors.length > 0 && (
          <div className="validation-errors">
            <Alert
              type="error"
              message={`${errors.length}件のエラーがあります`}
              description={
                <ul>
                  {errors.map((e, i) => (
                    <li key={i}>
                      行 {e.row} / {e.field}: {e.message}
                    </li>
                  ))}
                </ul>
              }
            />
          </div>
        )}

        <div className="modal-actions">
          <Button
            onClick={handleSubmit}
            className="btn-save"
            type="primary"
            loading={submitting}
            disabled={!selectedOffice || !file}
          >
            保存
          </Button>
        </div>
      </div>
    </Modal>
  );
};

ModalRegisterBranch.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
};

export default ModalRegisterBranch;
